import React, { useEffect, useState } from "react";
import { MapPin, Calendar, MessageSquareText, Languages, Wallet, Star, ShieldAlert, ChevronRight, Navigation, Map, AlertTriangle, Bell } from "lucide-react";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";
import { useAgencyNavigate, AgencyLink as Link } from "../lib/agency";
import { useSettings } from "../lib/settings";
import { getPushToken, getPlatform } from "../lib/native";

const MARKETING_CHOICE_KEY = "ts_marketing_push_choice";

const STATUS_LABEL = { pendiente: "Pendiente", en_curso: "En curso", completada: "Completada" };
const STATUS_CLASS = {
  pendiente: "bg-white/5 text-neutral-400 border-white/10",
  en_curso: "bg-[var(--brand-cyan,#FF85AC)]/15 text-[var(--brand-cyan,#FF85AC)] border-[var(--brand-cyan,#FF85AC)]/40",
  completada: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
};

const mapsUrl = (it) => {
  if (it.latitude && it.longitude) {
    return `https://www.google.com/maps/dir/?api=1&destination=${it.latitude},${it.longitude}`;
  }
  const q = encodeURIComponent(it.address || it.location || "");
  return `https://www.google.com/maps/dir/?api=1&destination=${q}`;
};

const wazeUrl = (it) => {
  if (it.latitude && it.longitude) {
    return `https://waze.com/ul?ll=${it.latitude},${it.longitude}&navigate=yes`;
  }
  const q = encodeURIComponent(it.address || it.location || "");
  return `https://waze.com/ul?q=${q}&navigate=yes`;
};

export default function Dashboard() {
  const { settings } = useSettings();
  const [itineraries, setItineraries] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [user, setUser] = useState(auth.current());
  const [showMarketingAsk, setShowMarketingAsk] = useState(false);
  const [marketingBusy, setMarketingBusy] = useState(false);
  const navigate = useAgencyNavigate();

  const loadItineraries = () => {
    setLoadError(false);
    api.get(`/itineraries/${user.booking_id}`)
      .then(({ data }) => setItineraries(data))
      // Sin esto, un fallo de red se veía idéntico a "no tienes servicios
      // cargados todavía" — un viajero real no puede distinguir un error de
      // que su agencia de verdad no subió nada.
      .catch(() => setLoadError(true));
  };

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    loadItineraries();
    // Antes esto registraba push de marketing en silencio con cada carga del
    // Dashboard (pidiendo permiso del navegador sin explicar nada) — mezclado
    // con la protección SOS, sin forma de aceptar una cosa sin la otra. Ahora
    // es una decisión explícita y separada, preguntada una sola vez.
    if (!localStorage.getItem(MARKETING_CHOICE_KEY)) setShowMarketingAsk(true);
  }, []);

  const answerMarketingAsk = async (accept) => {
    if (!accept) {
      localStorage.setItem(MARKETING_CHOICE_KEY, "no");
      setShowMarketingAsk(false);
      return;
    }
    setMarketingBusy(true);
    try {
      const fcmToken = await getPushToken();
      if (fcmToken) {
        await api.post("/me/register-device", {
          fcm_token: fcmToken,
          platform: getPlatform(),
          marketing_opt_in: true,
        });
      }
      localStorage.setItem(MARKETING_CHOICE_KEY, fcmToken ? "yes" : "denied");
    } catch {
      // El itinerario debe verse igual aunque el registro de push falle.
    } finally {
      setMarketingBusy(false);
      setShowMarketingAsk(false);
    }
  };

  if (!user) return null;

  const fileId = user.file_id || itineraries[0]?.file_id || "—";

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 fade-up">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">
              Booking {user.booking_id}
            </p>
            <h1 className="font-display text-5xl font-light tracking-tight">
              Hola, <span className="text-[var(--brand-cyan,#FF85AC)]">{user.traveler_name?.split(" ")[0]}</span>
            </h1>
            <p className="text-neutral-400 mt-3">Tu itinerario, herramientas y panel SOS.</p>
          </div>
          <Link
            to="/sos"
            data-testid="sos-shortcut"
            className="inline-flex items-center gap-3 px-5 py-3 rounded-full border border-[#FF3B30]/40 hover:bg-[#FF3B30]/10 text-[#FF3B30] transition-all"
          >
            <ShieldAlert className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.25em] font-bold">Panel SOS</span>
          </Link>
        </div>

        {showMarketingAsk && (
          <div
            className="glass rounded-3xl p-6 border border-white/10 mb-10 flex flex-col sm:flex-row sm:items-center gap-4 fade-up"
            data-testid="marketing-push-ask"
          >
            <Bell className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)] shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-neutral-300 flex-1">
              ¿Quieres recibir promociones y ofertas de {settings.agency_name} por notificación push?
              Esto es aparte de tu protección SOS, que ya funciona sin esto.
            </p>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => answerMarketingAsk(false)}
                disabled={marketingBusy}
                data-testid="marketing-push-decline"
                className="px-4 py-2 rounded-full border border-white/15 hover:border-white/30 text-sm text-neutral-400 disabled:opacity-50"
              >
                No, gracias
              </button>
              <button
                onClick={() => answerMarketingAsk(true)}
                disabled={marketingBusy}
                data-testid="marketing-push-accept"
                className="px-4 py-2 rounded-full bg-[var(--brand-cyan,#FF85AC)] text-black text-sm font-semibold hover:bg-[#FF9DC0] disabled:opacity-50"
              >
                Sí, avísame
              </button>
            </div>
          </div>
        )}

        {/* Master Trip ID Banner */}
        <div className="glass rounded-3xl p-6 md:p-8 border border-[var(--brand-cyan,#FF85AC)]/25 mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 fade-up" data-testid="master-trip-banner">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-2">Expediente Maestro</p>
            <p className="font-display text-3xl md:text-4xl tracking-wide text-[var(--brand-cyan,#FF85AC)]" data-testid="master-file-id">
              {fileId}
            </p>
          </div>
          <div className="text-sm text-neutral-400">
            <p>{itineraries.length} servicios incluidos</p>
            {itineraries[0] && (
              <p className="text-xs text-neutral-500 mt-1">
                {itineraries[0].start_date} → {itineraries[itineraries.length - 1]?.end_date}
              </p>
            )}
          </div>
        </div>

        {/* Quick Tools */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-12">
          {[
            { to: "/chat", icon: MessageSquareText, label: "Chat" },
            { to: "/tools", icon: Languages, label: "Traducir" },
            { to: "/tools", icon: Wallet, label: "Moneda" },
            { to: "/feedback", icon: Star, label: "Feedback" },
          ].map((t, i) => (
            <Link
              key={i}
              to={t.to}
              data-testid={`tool-${t.label.toLowerCase()}`}
              className="group glass rounded-2xl p-5 border border-white/8 hover:border-[var(--brand-cyan,#FF85AC)]/40 transition-all"
            >
              <t.icon className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)] mb-3" strokeWidth={1.4} />
              <p className="text-sm text-white">{t.label}</p>
            </Link>
          ))}
        </div>

        {/* Timeline */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-2xl tracking-tight">Itinerario</h2>
          <p className="text-xs uppercase tracking-[0.2em] text-neutral-500">
            {itineraries.length} servicios
          </p>
        </div>

        {loadError ? (
          <div className="glass rounded-3xl p-10 text-center border border-[#FFB020]/30" data-testid="itinerary-error">
            <AlertTriangle className="w-6 h-6 text-[#FFB020] mx-auto mb-3" />
            <p className="text-neutral-300 mb-4">No se pudo cargar tu itinerario. Revisa tu conexión.</p>
            <button
              onClick={loadItineraries}
              className="px-5 py-2 rounded-full border border-[#FFB020]/40 text-[#FFB020] text-sm hover:bg-[#FFB020]/10"
            >
              Reintentar
            </button>
          </div>
        ) : itineraries.length === 0 ? (
          <div className="glass rounded-3xl p-10 text-center text-neutral-500">
            Aún no hay servicios cargados. El admin debe subir el PDF de Regio.
          </div>
        ) : (
          <ol className="relative border-l border-white/10 ml-3 space-y-6" data-testid="itinerary-list">
            {itineraries.map((it, i) => (
              <li key={it.id} className="pl-8 relative">
                <span className="absolute -left-[7px] top-2 w-3 h-3 rounded-full bg-[var(--brand-cyan,#FF85AC)] cyan-glow" />
                <article className="glass rounded-2xl p-6 border border-white/8 hover:border-[var(--brand-cyan,#FF85AC)]/30 transition-all" data-testid={`itinerary-${i}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                        Servicio <span className="text-[var(--brand-cyan,#FF85AC)]">{it.service_code || `S${(i+1).toString().padStart(2,'0')}`}</span>
                      </p>
                      <h3 className="font-display text-xl text-white">{it.title}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      {it.status && (
                        <span
                          data-testid={`service-status-${i}`}
                          className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] border whitespace-nowrap ${STATUS_CLASS[it.status] || STATUS_CLASS.pendiente}`}
                        >
                          {STATUS_LABEL[it.status] || it.status}
                        </span>
                      )}
                      <ChevronRight className="text-neutral-600 w-5 h-5 shrink-0" />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-400 mb-4">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-[var(--brand-cyan,#FF85AC)]/70" /> {it.location}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-[var(--brand-cyan,#FF85AC)]/70" />
                      {it.start_date}{it.end_date && it.end_date !== it.start_date ? ` → ${it.end_date}` : ""}
                    </span>
                  </div>

                  {it.description && (
                    <p className="text-sm text-neutral-300 leading-relaxed mb-4">{it.description}</p>
                  )}

                  {/* Maps actions */}
                  {(it.location || it.address) && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      <a
                        href={mapsUrl(it)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`maps-btn-${i}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--brand-cyan,#FF85AC)]/10 border border-[var(--brand-cyan,#FF85AC)]/40 text-[var(--brand-cyan,#FF85AC)] text-xs uppercase tracking-[0.2em] hover:bg-[var(--brand-cyan,#FF85AC)]/20 transition-all"
                      >
                        <Navigation className="w-3 h-3" /> Cómo llegar
                      </a>
                      <a
                        href={wazeUrl(it)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`waze-btn-${i}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 hover:border-[var(--brand-cyan,#FF85AC)]/40 text-neutral-300 text-xs uppercase tracking-[0.2em] transition-all"
                      >
                        <Map className="w-3 h-3" /> Waze
                      </a>
                    </div>
                  )}

                  {it.supplier_notes && (
                    <div className="mt-3 p-3 rounded-xl bg-black/40 border border-white/5">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1">
                        Nota del proveedor
                      </p>
                      <p className="text-sm text-neutral-400 italic">"{it.supplier_notes}"</p>
                      <Link
                        to={`/tools?text=${encodeURIComponent(it.supplier_notes)}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] text-[var(--brand-cyan,#FF85AC)] hover:underline mt-2"
                        data-testid={`translate-link-${i}`}
                      >
                        <Languages className="w-3 h-3" /> Traducir
                      </Link>
                    </div>
                  )}
                </article>
              </li>
            ))}
          </ol>
        )}
      </section>
    </Layout>
  );
}
